import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'wouter';
import type { Lang } from '@polyglot/engine';
import { BackLink, LangName, Spinner } from '../components/ui';
import { api, ApiError, ensureSession, useMe } from '../lib/api';
import { formatScore } from '../lib/format';

interface BoardEntry {
  rank: number;
  displayName: string;
  rating: number;
  games: number;
  wins: number;
  isMe: boolean;
}

export function Leaderboards() {
  const { t } = useTranslation();
  const [board, setBoard] = useState<'official' | 'experimental'>('official');
  const [data, setData] = useState<{
    entries: BoardEntry[];
    me: { rank: number; rating: number; games: number } | null;
  } | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    setData(null);
    api<typeof data>('GET', `/api/leaderboards/${board}`)
      .then(setData)
      .catch(() => setError(true));
  }, [board]);
  return (
    <main className="page">
      <BackLink />
      <h1>{t('boards.title')}</h1>
      <div className="row" role="tablist">
        {(['official', 'experimental'] as const).map((b) => (
          <button
            key={b}
            role="tab"
            aria-selected={board === b}
            className={`chip${board === b ? ' selected' : ''}`}
            onClick={() => setBoard(b)}
          >
            {t(`boards.${b}`)}
          </button>
        ))}
      </div>
      <p className="muted small">{t(`boards.${board}Desc`)}</p>
      {error && <p className="message error">{t('online.offlineNote')}</p>}
      {!data && !error && <Spinner label={t('common.loading')} />}
      {data && (
        <>
          {data.me && (
            <p>
              {t('boards.myRank', {
                rank: data.me.rank,
                rating: data.me.rating,
                games: data.me.games,
              })}
            </p>
          )}
          {data.entries.length === 0 ? (
            <p className="muted">{t('boards.empty')}</p>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('boards.player')}</th>
                  <th className="num">{t('boards.rating')}</th>
                  <th className="num">{t('boards.games')}</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e, i) => (
                  <tr key={i} className={e.isMe ? 'me' : undefined}>
                    <td>{e.rank}</td>
                    <td>{e.displayName}</td>
                    <td className="num">{e.rating}</td>
                    <td className="num">{e.games}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
      <p className="muted small">{t('boards.rules')}</p>
    </main>
  );
}

interface TournamentRow {
  id: string;
  title: string;
  category: string;
  mode: string;
  shared_languages: Lang[];
  round_count: number;
  starts_at: string;
  ends_at: string;
  timezone: string;
  status: string;
  visibility: string;
  entrants: number;
  registered: boolean;
}

export function competitionTitle(
  t: (k: string, o?: Record<string, unknown>) => string,
  title: string,
): string {
  const [kind, category, period] =
    title.split(':').length >= 3
      ? [title.split(':')[0], title.split(':').slice(1, -1).join(':'), title.split(':').at(-1)]
      : [null, null, null];
  if (kind === 'weekly' && category && period) {
    const [mode, langs] = category.split(':');
    return t('tournaments.weeklyTitle', {
      mode: t(`modes.${mode}.name`),
      langs: (langs ?? '').toUpperCase(),
      period,
    });
  }
  return title;
}

function dateRange(start: string, end: string, locale: string, tz: string): string {
  const f = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });
  const u = new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: tz,
  });
  return `${f.format(new Date(start))} → ${f.format(new Date(end))} (${u.format(new Date(end))} ${tz})`;
}

export function Tournaments() {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<TournamentRow[] | null>(null);
  useEffect(() => {
    api<{ tournaments: TournamentRow[] }>('GET', '/api/tournaments')
      .then((r) => setRows(r.tournaments))
      .catch(() => setRows([]));
  }, []);
  return (
    <main className="page">
      <BackLink />
      <h1>{t('tournaments.title')}</h1>
      <p className="muted small">{t('tournaments.intro')}</p>
      {!rows && <Spinner label={t('common.loading')} />}
      {rows && rows.length === 0 && <p className="muted">{t('tournaments.none')}</p>}
      {rows?.map((r) => (
        <Link key={r.id} href={`/tournaments/${r.id}`} className="card tournament-card">
          <strong>{competitionTitle(t, r.title)}</strong>
          <span className="muted small">
            {t(`tournaments.status.${r.status}`)} · {t('tournaments.rounds', { n: r.round_count })}{' '}
            · {t('tournaments.entrants', { n: r.entrants })}
            {r.registered ? ` · ${t('tournaments.registered')}` : ''}
          </span>
          <span className="muted small">
            {dateRange(r.starts_at, r.ends_at, i18n.language, r.timezone)}
          </span>
        </Link>
      ))}
    </main>
  );
}

interface TournamentView {
  id: string;
  kind: string;
  title: string;
  mode: string;
  sharedLanguages: Lang[];
  rounds: number;
  maxAttemptsPerRound: number;
  startsAt: string;
  endsAt: string;
  timezone: string;
  status: string;
  rulesVersion: string;
  lexiconVersions: Record<string, string>;
  requiresAccount: boolean;
  standings: {
    rank: number;
    displayName: string;
    totalCents: number;
    roundsPlayed: number;
    isMe: boolean;
  }[];
  me: {
    registered: boolean;
    attempts: RoundAttempt[];
  } | null;
}

type RoundAttempt = {
  round_no: number;
  attempt: number;
  match_id: string;
  status: string;
  final_cents: number | null;
};

/** State of one round for the player: best finished score, attempt to resume, attempts left. */
export function roundState(attempts: RoundAttempt[], round: number, maxAttempts: number) {
  const mine = attempts.filter((a) => a.round_no === round);
  const finished = mine.filter((a) => a.status === 'finished' && a.final_cents !== null);
  const best = finished.length ? Math.max(...finished.map((a) => a.final_cents!)) : null;
  const open = mine.find((a) => a.status !== 'finished' && a.status !== 'abandoned') ?? null;
  return { best, open, left: Math.max(0, maxAttempts - mine.length), tried: mine.length > 0 };
}

export function TournamentDetail({ params }: { params: { id: string } }) {
  const { t, i18n } = useTranslation();
  const [, navigate] = useLocation();
  const { user } = useMe();
  const [view, setView] = useState<TournamentView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api<TournamentView>('GET', `/api/tournaments/${params.id}`)
      .then((v) => {
        setView(v);
        setLoadError(null);
      })
      .catch((e: unknown) =>
        setLoadError(
          e instanceof ApiError && e.status === 0
            ? t('online.offlineNote')
            : t('tournaments.errors.competition_not_found'),
        ),
      );
  }, [params.id, t]);
  useEffect(() => {
    void ensureSession().then(load, load);
  }, [load]);

  if (loadError)
    return (
      <main className="page">
        <BackLink to="/tournaments" />
        <p className="message error">{loadError}</p>
        <button className="btn ghost" onClick={load}>
          {t('common.retry')}
        </button>
      </main>
    );
  if (!view) return <Spinner label={t('common.loading')} />;
  const open = view.status === 'open';
  const closed = view.status === 'closed';
  const errorText = (e: unknown) =>
    e instanceof ApiError
      ? e.status === 0
        ? t('online.offlineNote')
        : t(`tournaments.errors.${e.code}`, { defaultValue: t('errors.generic') })
      : t('errors.generic');
  const act = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setActionError(null);
    try {
      await fn();
    } catch (e) {
      setActionError(errorText(e));
      load();
    } finally {
      setBusy(false);
    }
  };
  const play = (n: number) =>
    act(async () => {
      const r = await api<{ matchId: string }>(
        'POST',
        `/api/tournaments/${view.id}/rounds/${n}/play`,
        {},
      );
      navigate(`/match/${r.matchId}`);
    });
  const me = view.standings.find((s) => s.isMe);
  return (
    <main className="page">
      <BackLink to="/tournaments" />
      <h1>{competitionTitle(t, view.title)}</h1>
      <p className="muted">
        <span className={`status status-${view.status}`}>
          {t(`tournaments.status.${view.status}`)}
        </span>
        {view.status === 'scheduled' &&
          ` · ${t('tournaments.startsAt', {
            date: new Date(view.startsAt).toLocaleString(i18n.language),
          })}`}
      </p>
      <section className="card">
        <p>
          <strong>{t(`modes.${view.mode}.name`)}</strong> —{' '}
          {view.sharedLanguages.map((l) => (
            <LangName key={l} lang={l} />
          ))}
        </p>
        <p className="muted small">
          {dateRange(view.startsAt, view.endsAt, i18n.language, view.timezone)}
        </p>
        <p className="muted small">
          {t('tournaments.frozen', {
            rules: view.rulesVersion,
            lexicons: Object.values(view.lexiconVersions).join(', '),
          })}
        </p>
        <p className="muted small">{t('tournaments.attempts', { n: view.maxAttemptsPerRound })}</p>
        <p className="muted small">{t('tournaments.sharingLimit')}</p>
      </section>
      {view.status === 'closing' && <p className="message">{t('tournaments.closingNote')}</p>}
      {actionError && (
        <p className="message error" role="alert">
          {actionError}
        </p>
      )}
      {view.me &&
        !view.me.registered &&
        open &&
        (user?.kind === 'registered' ? (
          <button
            className="btn primary big"
            disabled={busy}
            onClick={() =>
              void act(async () => {
                await api('POST', `/api/tournaments/${view.id}/register`, {});
                load();
              })
            }
          >
            {t('tournaments.register')}
          </button>
        ) : (
          <aside className="card">
            <p>{t('tournaments.accountRequired')}</p>
            <Link className="btn primary" href="/account">
              {t('online.saveAccount')}
            </Link>
          </aside>
        ))}
      {view.me?.registered && (
        <section className="card my-rounds">
          <h2>{t('tournaments.myRounds')}</h2>
          {Array.from({ length: view.rounds }, (_, i) => i + 1).map((n) => {
            const r = roundState(view.me!.attempts, n, view.maxAttemptsPerRound);
            return (
              <div key={n} className="row round-row" data-round={n}>
                <span>{t('tournaments.round', { n })}</span>
                {r.best !== null && <strong>{formatScore(r.best, i18n.language)}</strong>}
                {r.open ? (
                  <Link className="btn small primary" href={`/match/${r.open.match_id}`}>
                    {t('tournaments.resume')}
                  </Link>
                ) : open && r.left > 0 ? (
                  <button
                    className="btn small primary"
                    disabled={busy}
                    onClick={() => void play(n)}
                  >
                    {r.tried ? t('tournaments.retry', { n: r.left }) : t('tournaments.play')}
                  </button>
                ) : r.best === null ? (
                  <span className="muted">{r.tried ? t('tournaments.abandoned') : '—'}</span>
                ) : null}
              </div>
            );
          })}
          <p className="muted small my-total">
            {me && me.roundsPlayed > 0
              ? t('tournaments.myResult', {
                  total: formatScore(me.totalCents, i18n.language),
                  rank: me.rank,
                  n: view.standings.length,
                })
              : t('tournaments.noRoundYet')}
          </p>
        </section>
      )}
      <section className="card standings">
        <h2>{closed ? t('tournaments.finalStandings') : t('tournaments.standings')}</h2>
        {view.standings.length === 0 ? (
          <p className="muted">{t('boards.empty')}</p>
        ) : (
          <table className="data">
            <tbody>
              {view.standings.map((s, i) => (
                <tr key={i} className={s.isMe ? 'me' : undefined}>
                  <td>{s.rank}</td>
                  <td>{s.displayName}</td>
                  <td className="num">{formatScore(s.totalCents, i18n.language)}</td>
                  <td className="num muted">
                    {t('tournaments.roundsPlayed', { n: s.roundsPlayed })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="muted small">{t('tournaments.tieRule')}</p>
      </section>
    </main>
  );
}

interface DailyRow {
  id: string;
  category: string;
  mode: string;
  sharedLanguages: Lang[];
  day: string;
  endsAt: string;
  played: boolean;
  finalCents: number | null;
}

export function Daily() {
  const { t, i18n } = useTranslation();
  const [, navigate] = useLocation();
  const [rows, setRows] = useState<DailyRow[] | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    ensureSession()
      .then(() => api<{ daily: DailyRow[] }>('GET', '/api/daily'))
      .then((r) => setRows(r.daily))
      .catch(() => setError(true));
  }, []);
  return (
    <main className="page">
      <BackLink />
      <h1>{t('daily.title')}</h1>
      <p className="muted small">{t('daily.intro')}</p>
      {error && <p className="message error">{t('online.offlineNote')}</p>}
      {!rows && !error && <Spinner label={t('common.loading')} />}
      <div className="modes-grid">
        {rows?.map((r) => (
          <button
            key={r.id}
            className="mode-card"
            // An attempt in progress can be resumed (the server returns the same match).
            disabled={r.played && r.finalCents !== null}
            onClick={async () => {
              try {
                const m = await api<{ matchId: string }>('POST', `/api/daily/${r.id}/play`, {});
                navigate(`/match/${m.matchId}`);
              } catch {
                setError(true);
              }
            }}
          >
            <strong>
              {t(`modes.${r.mode}.name`)} ·{' '}
              {r.sharedLanguages.map((l) => l.toUpperCase()).join('+')}
            </strong>
            <span className="muted small">
              {r.played
                ? r.finalCents !== null
                  ? formatScore(r.finalCents, i18n.language)
                  : t('daily.inProgress')
                : t('daily.play')}
            </span>
          </button>
        ))}
      </div>
      {rows?.[0] && (
        <p className="muted small">
          {t('daily.resets', { time: new Date(rows[0].endsAt).toLocaleString(i18n.language) })}
        </p>
      )}
    </main>
  );
}
